import { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from "react"
import TagManager from "react-gtm-module"
import { BiErrorAlt } from "react-icons/bi"
import AutosizeInput from "react-input-autosize"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"

import Button from "@fuse/sharedComponents/buttons/SolidBtn"
import LoadingSpinner from "@fuse/sharedComponents/loader/LoadingSpinner"
import { showErrorToast } from "@fuse/sharedComponents/themeToast/toastUtils"
import FancyText from "@fuse/sharedComponents/typography/FancyText"
import { countryDobFormat } from "@letshang/assets"
import { TTemplateEvent, TWebUser } from "@letshang/constants"
import moment from "moment"
import { useRecoilState } from "recoil"

import { screenState, userState } from "@fuse/recoil/signup"

import {
  ELogAnalyticEvent,
  ELogGTMEvent,
  logAnalyticEvent,
} from "@firebaseConfig/analyticsUtil"

import { useSignup } from "@fuse/pages/signUp/controller"

export type DobType = {
  day: string
  month: string
  year: string
}

const AutosizeInputText = AutosizeInput as any

interface DateInputConfig {
  placeholder: string
  maxLength: number
  order: number
}

export function capitalizeSentence(sentence: string) {
  return sentence.toUpperCase()
}

// Helper functions
export const getDateFormatForCountry = (countryCode: string = "US"): string => {
  return countryDobFormat[countryCode] || countryDobFormat.DEFAULT
}

export const parseDateParts = (
  day: string,
  month: string,
  year: string,
  format: string,
): string => {
  switch (format) {
    case "MM/DD/YYYY":
      return `${month}/${day}/${year}`
    case "YYYY/MM/DD":
      return `${year}/${month}/${day}`
    case "DD/MM/YYYY":
    default:
      return `${day}/${month}/${year}`
  }
}

export const validateDate = (
  day: string,
  month: string,
  year: string,
  format: string,
): boolean => {
  const dateString = parseDateParts(day, month, year, format)
  return moment(dateString, format.replace(/\//g, "")).isValid()
}

export const getInputConfig = (
  format: string,
): Record<string, DateInputConfig> => {
  // Defensive check: fallback to US format if format is undefined/null
  const safeFormat = format || "MM/DD/YYYY"
  const parts = safeFormat.split("/")
  const config: Record<string, DateInputConfig> = {}

  parts.forEach((part, index) => {
    switch (part) {
      case "DD":
        config.day = { placeholder: "dd", maxLength: 2, order: index }
        break
      case "MM":
        config.month = { placeholder: "mm", maxLength: 2, order: index }
        break
      case "YYYY":
        config.year = { placeholder: "yyyy", maxLength: 4, order: index }
        break
    }
  })

  return config
}

const Dob = ({ template }: { template: TTemplateEvent }) => {
  const [, setStep] = useRecoilState(screenState)
  const [user, setUser] = useRecoilState(userState)
  const { isLoading, updateUser } = useSignup()
  const navigate = useNavigate()

  const [isValidDate, setIsValidDate] = useState(true)
  const [formattedDate, setFormattedDate] = useState<string | null>(null)
  const [dateErrorText, setDateErrorText] = useState<string | null>(null)

  const dateFormat = getDateFormatForCountry(user.country)
  const inputConfig = getInputConfig(dateFormat)
  // const formatHelperText = `Format: ${dateFormat}`;

  const [dob, setDob] = useState<DobType>({
    day: "",
    month: "",
    year: "",
  })

  const [modalType, setModalType] = useState<"info" | "error" | null>(null)

  const isButtonEnabled =
    dob.day.length < 2 ||
    dob.month.length < 2 ||
    dob.year.length < 4 ||
    !isValidDate

  const refs = {
    day: useRef<HTMLInputElement>(null),
    month: useRef<HTMLInputElement>(null),
    year: useRef<HTMLInputElement>(null),
  }

  const getNextInput = (currentKey: string): keyof typeof refs | null => {
    const sortedInputs = Object.entries(inputConfig)
      .sort(([, a], [, b]) => a.order - b.order)
      .map(([key]) => key)

    const currentIndex = sortedInputs.indexOf(currentKey)
    return currentIndex < sortedInputs.length - 1
      ? (sortedInputs[currentIndex + 1] as keyof typeof refs)
      : null
  }

  const getPrevInput = (currentKey: string): keyof typeof refs | null => {
    const sortedInputs = Object.entries(inputConfig)
      .sort(([, a], [, b]) => a.order - b.order)
      .map(([key]) => key)

    const currentIndex = sortedInputs.indexOf(currentKey)
    return currentIndex > 0
      ? (sortedInputs[currentIndex - 1] as keyof typeof refs)
      : null
  }

  const handleKeyDown = (
    key: keyof DobType,
    e: KeyboardEvent<HTMLInputElement>,
  ) => {
    const nextInput = getNextInput(key)
    const prevInput = getPrevInput(key)
    const maxLength = inputConfig[key].maxLength

    if (e.currentTarget.value.length >= maxLength && e.key !== "Backspace") {
      nextInput && refs[nextInput].current?.focus()
    }
    if (e.key === "Backspace" && e.currentTarget.value === "") {
      prevInput && refs[prevInput].current?.focus()
    }
  }

  const handleInputChange = (
    key: keyof DobType,
    e: ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value
    const maxVal =
      key === "month" ? 12 : key === "day" ? 31 : new Date().getFullYear()

    if (Number(value) > maxVal) {
      e.target.value = maxVal.toString()
    }

    setDob((prev) => ({ ...prev, [key]: e.target.value }))
  }

  const handleNext = async () => {
    if (Number(dob.year) > new Date().getFullYear() - 18) {
      setModalType("error")
      return
    }

    const dateString = parseDateParts(dob.day, dob.month, dob.year, dateFormat)
    if (!validateDate(dob.day, dob.month, dob.year, dateFormat)) {
      setModalType("error")
      return
    }

    const dobInterpretationFormat = getDateFormatForCountry(
      user.country,
    ).replace("/", "-")
    const formattedDOB = moment(dateString, dobInterpretationFormat).format(
      "YYYY-MM-DD",
    )

    const userObj = {
      name: user.name,
      dateOfBirth: formattedDOB,
      gender: user.gender,
      expoPushToken: user.expoPushToken,
      country: user.country,
      phoneNumber: user.phone,
    }

    try {
      const res: any = await updateUser({ ...userObj, eventId: user.eventId })

      if (res.success) {
        setUser((prev: TWebUser) => ({
          ...prev,
          ...userObj,
          isOnboarded: true,
          isLoggedIn: true,
        }))

        logAnalyticEvent(ELogAnalyticEvent.SIGN_UP_WEB, {
          uid: user.uid,
          page_name: "Sign up",
          action: "completed sign up with all fields",
        })

        dynamicNav()
      } else {
        console.error("Error updating user details")
        showErrorToast(res.message)
      }
    } catch (e) {
      console.error(e)
    }
  }

  useEffect(() => {
    if (user.dob) {
      const dobArr = user.dob.split("/")
      const format = getDateFormatForCountry(user.country)

      if (format === "MM/DD/YYYY") {
        setDob({ month: dobArr[0], day: dobArr[1], year: dobArr[2] })
      } else if (format === "DD/MM/YYYY") {
        setDob({ day: dobArr[0], month: dobArr[1], year: dobArr[2] })
      } else {
        setDob({ year: dobArr[0], month: dobArr[1], day: dobArr[2] })
      }
    }
  }, [user])

  useEffect(() => {
    const validateAndFormatDate = (): string | null => {
      setDateErrorText("")
      setIsValidDate(true)
      const dateString = parseDateParts(
        dob.day,
        dob.month,
        dob.year,
        dateFormat,
      )
      if (dateString.length === 10) {
        const date = moment(dateString, dateFormat, true)

        // Check if the date is invalid
        if (!date.isValid()) {
          setIsValidDate(false)
          setDateErrorText("Date is not valid")
          return null
        }

        // Check if the date is in the future
        if (date.isAfter(moment())) {
          setIsValidDate(false)
          setDateErrorText("Future dates are not allowed")
          return null
        }

        // Check if the age is less than 18
        const age = moment().diff(date, "years")
        if (age < 18) {
          setIsValidDate(false)
          setDateErrorText("You must be at least 18 years old")
          return null
        }

        // Check if the age exceeds 80
        if (age > 80) {
          setIsValidDate(false)
          setDateErrorText("Age cannot exceed 80 years")
          return null
        }

        // If all validations pass
        setIsValidDate(true)
        setDateErrorText("")
        return date.format("MMMM DD, YYYY")
      }
    }

    // Perform validation and set formatted date
    setFormattedDate(validateAndFormatDate())
  }, [dob, dateFormat])

  const dynamicNav = () => {
    const url = window.location.href

    const queryParams = url.split("?")[1]

    const action = queryParams
      ?.split("&")
      .find((param) => param.startsWith("action="))
      .substring(7)

    let redirect = "/"

    switch (action) {
      case "create-event":
        redirect = "/create-event"
        break

      case "create-event-v3":
        redirect = "/create-event-v3"
        break

      case "create-event-v4":
        redirect = "/create-event-v4"
        break

      case "explore":
        redirect = "/explore"
        break

      case "people":
        redirect = "/people"
        break

      case "event-templates":
        redirect = "/event-templates"
        break

      default:
        redirect = "/"
        break
    }

    navigate(user.redirectUrl || redirect, {
      state: {
        template: template || {},
      },
    })
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen w-full">
      <div className="w-[90%] max-w-sm mb-5 md:mb-10 text-start">
        <p className="text-white/30 text-left text-sm mt-10 md:mt-0 lg:mt-0 font-medium">
          STEP 2
        </p>
        <FancyText className="text-xl md:text-2.5xl mt-[10px] text-white font-semibold">
          Setting up your profile
        </FancyText>
      </div>
      <div className="relative z-[10]">{isLoading && <LoadingSpinner />}</div>
      <div className="w-[90%] max-w-sm bg-white/10 relative rounded-2xl p-10 space-y-6">
        {/* Title */}
        <div className="text-left">
          <h2 className="text-white text-2xl font-semibold">
            Thanks!{" "}
            <span className="bg-gradient-to-r from-[#C190FF] to-[#A9A4FF] inline-block text-transparent bg-clip-text">
              How old
            </span>{" "}
            are you?
          </h2>
          <p className="text-white/35 mt-2">
            We collect your age to ensure eligibility. Your info stays private.
          </p>
        </div>

        <div className="flex flex-col items-start pb-24 w-full justify-center">
          <div
            className={`px-4 bg-white/20 h-12 rounded-md flex justify-center w-full items-center ${isValidDate ? "" : "border"} border-red-400`}
          >
            <div className="flex items-center w-full justify-start">
              {Object.entries(inputConfig)
                .sort(([, a], [, b]) => a.order - b.order)
                .map(([key, config], index, array) => (
                  <div key={key} className="flex items-start flex-shrink-0">
                    <AutosizeInputText
                      inputRef={(input: any) => {
                        refs[key as keyof typeof refs].current = input
                      }}
                      name="form-field-name"
                      // type="number"
                      inputMode="numeric"
                      placeholder={capitalizeSentence(config.placeholder)}
                      value={dob[key as keyof DobType]}
                      maxLength={config.maxLength}
                      onChange={(e: any) =>
                        handleInputChange(key as keyof DobType, e)
                      }
                      className="h-full text-[16px] text-black placeholder-gray-500 outline-none text-left w-auto max-w-none flex-shrink-0 placeholder:capitalize"
                      onKeyDown={(e: any) =>
                        handleKeyDown(key as keyof DobType, e)
                      }
                      inputStyle={styles}
                    />
                    {index < array.length - 1 && (
                      <span className="text-[15px] text-white opacity-[0.3]">
                        /
                      </span>
                    )}
                  </div>
                ))}
            </div>
            {!isValidDate && <BiErrorAlt className="text-red-400 text-2xl" />}
          </div>
          <p
            className={`text-white/35 mt-2 ${isValidDate ? "" : "text-[#f87171]"}`}
          >
            {isValidDate ? formattedDate : dateErrorText}
          </p>
        </div>

        <button
          className={`w-full bg-white/20 text-white py-3 rounded-full font-medium transition ${!isButtonEnabled ? "" : "opacity-50"}`}
          onClick={handleNext}
          disabled={
            dob.day.length < 2 ||
            dob.month.length < 2 ||
            dob.year.length < 4 ||
            !isValidDate
          }
        >
          Continue
        </button>
      </div>
    </div>
  )
}

export default Dob

const styles = {
  color: "white",
  backgroundColor: "transparent",
  outline: "none",
  boxShadow: "none",
}
